import { useMemo, useState } from 'react';
import { When } from 'react-if';
import {
  ArrowUturnLeftIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  ClockIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  PencilIcon,
  PhoneIcon,
} from '@heroicons/react/20/solid';
import {
  ActionStatus,
  ActionType,
  ActivityType,
  activityLabel,
  CommunicationType,
  failedEmailEventTypes,
  IActionResponseItem,
  INote,
  langauge,
} from 'shared';

import { useAccountingSystemName } from '../../../../hooks/useAccountingSystemName';
import { useCommunications } from '../../../../hooks/useCommunications';
import { useContact } from '../../../../hooks/useContact';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { useIsSafeMode } from '../../../../hooks/useIsSafeMode';
import { useCreateContactNoteMutation, useFetchContactActivitiesQuery } from '../../../../services/api/contactsApi/contact';
import {
  ActivityFeedTimeline,
  IFeedItem,
  noteItem,
  SegmentedControl,
} from '../../../Molecules/ActivityFeedTimeline';
import { CallTranscript } from '../../../Molecules/CallTranscript';
import { NoteComposer } from '../../../Molecules/Notes';
import { Message as ActionMessage } from '../../../TimeLine/TimelineBasicItem';
import { WrappedLink } from '../../../WrappedLink';
import { LogActivityModal } from '../LogActivityModal';

import { useContactAllActions, useContactAllEscalations } from './contactPageData';

type FeedKind = `comms` | `actions` | `escalations` | `notes`;
type FeedFilter = `all` | FeedKind;

interface FeedItem extends IFeedItem {
  /** An item can count as more than one kind, e.g. a reminder action is also the email it sent */
  kinds: FeedKind[];
  /** Drafted or scheduled action, hidden unless "Show future" is ticked */
  future?: boolean;
}

interface PendingNote {
  id: number;
  content: string;
  createdAt: Date;
  /** Number of notes on the contact when this was submitted, hidden once the real note arrives */
  notesBefore: number;
}

const filters: { id: FeedFilter; label: string }[] = [
  { id: `all`, label: `All` },
  { id: `comms`, label: `Comms` },
  { id: `actions`, label: `Actions` },
  { id: `escalations`, label: `Escalations` },
  { id: `notes`, label: `Notes` },
];

const linkClass = `text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline`;

function InvoiceLink({ id, number }: { id?: string; number?: string }) {
  if (!id) return null;

  return (
    <WrappedLink
      to={ `/invoices/${id}` }
      className={ linkClass }
    >
      { number || `Invoice` }
    </WrappedLink>
  );
}

/** Joins the parts of a detail line with " · ", skipping empty ones */
function detailLine(...parts: React.ReactNode[]) {
  const present = parts.filter(Boolean);

  if (!present.length) return null;

  return present.map((part, i) => (
    <span key={ i }>
      { i > 0 ? ` · ` : `` }
      { part }
    </span>
  ));
}

function actionItem(action: IActionResponseItem, safemode: boolean, now: Date): FeedItem {
  const scheduled = [ActionStatus.PENDING, ActionStatus.APPROVAL_PENDING].includes(action.status);
  const dueNow = action.status === ActionStatus.PENDING && new Date(action.actionOn) < now && !safemode;
  const warning = [ActionStatus.FAILED, ActionStatus.SUSPENDED].includes(action.status);

  return {
    key: `action-${action.id}`,
    kinds: [`actions`],
    at: dueNow ? now : new Date(action.executedOn || action.actionOn || action.createdAt),
    icon: action.status === ActionStatus.COMPLETE ? CheckIcon : warning ? ExclamationTriangleIcon : ClockIcon,
    iconClass: action.status === ActionStatus.COMPLETE ? `bg-green-500` : warning ? `bg-orange-500` : `bg-gray-400`,
    title: <ActionMessage action={ action } />,
    detail: action.invoiceId ? (
      <InvoiceLink
        id={ action.invoiceId }
        number={ action.invoice?.xero_number }
      />
    ) : null,
    timeLabel: dueNow ? `Within 5 minutes` : undefined,
    pending: scheduled,
    future: scheduled,
  };
}

/** Comms, Paidnice actions, escalations and notes for the contact in one thread, oldest first */
export function ContactActivityFeed() {
  const organisation = useGetSelectedOrganisation();
  const { data: contact } = useContact();
  const { data: actionsData, isLoading: actionsLoading } = useContactAllActions();
  const { escalations, isLoading: escalationsLoading } = useContactAllEscalations();
  const { data: commsData, isLoading: commsLoading } = useCommunications();
  const { data: activitiesData, isLoading: activitiesLoading } = useFetchContactActivitiesQuery({
    organisationId: contact?.organisationId,
    contactId: contact?.id,
  }, { skip: !contact });
  const [postNewNote, { isLoading: postingNote }] = useCreateContactNoteMutation();
  const accountingName = useAccountingSystemName();
  const safemode = useIsSafeMode();
  const [filter, setFilter] = useState<FeedFilter>(`all`);
  const [showFuture, setShowFuture] = useState(false);
  /** Which activity type the Record Activity modal is open for */
  const [logType, setLogType] = useState<ActivityType | null>(null);
  const [pendingNotes, setPendingNotes] = useState<PendingNote[]>([]);
  // Bumped when the user adds something, so the thread scrolls down to it
  const [follows, setFollows] = useState(0);

  const timezone = organisation?.validatedTimezone;
  const notes: INote[] = useMemo(() => contact?.notes || [], [contact?.notes]);

  const items = useMemo(() => {
    const now = new Date();
    const all: FeedItem[] = [];
    const actions = actionsData?.actions || [];
    const actionsById = new Map(actions.map(action => [String(action.id), action]));
    const actionEmailIds = new Set(actions.map(action => action.sendgridMessage?.internalId).filter(Boolean));
    // Actions whose email or SMS is also in the comms list. The action row stands in for both
    const actionsWithComms = new Set<string>();
    // On demand actions are the other way round, the comm row stands in for both
    const actionsReplacedByComms = new Set<string>();

    (commsData?.communications || []).forEach((comm, i) => {
      if (!(comm.email || comm.sms || comm.inboundEmail)?.createdAt) return;

      if (comm.type === CommunicationType.EMAIL && comm.email) {
        const email = comm.email;

        const action = actionEmailIds.has(email.sendgridMessageInternalId)
          ? actions.find(a => a.sendgridMessage?.internalId === email.sendgridMessageInternalId)
          : undefined;

        if (action) {
          if (action.type !== ActionType.ON_DEMAND) {
            actionsWithComms.add(String(action.id));

            return;
          }

          actionsReplacedByComms.add(String(action.id));
        }

        const failed = failedEmailEventTypes.includes(email.latestStatus);

        all.push({
          key: `email-${email.sendgridMessageInternalId}`,
          // Stands in for its on demand action too
          kinds: action ? [`comms`, `actions`] : [`comms`],
          at: new Date(email.createdAt),
          icon: failed ? ExclamationTriangleIcon : EnvelopeIcon,
          iconClass: failed ? `bg-orange-500` : `bg-blue-500`,
          title: failed ? `Email to ${email.to} failed (${email.latestStatus})` : `Email sent to ${email.to}`,
          detail: email.emailContent?.subject || null,
        });

        return;
      }

      if (comm.type === CommunicationType.SMS && comm.sms) {
        const sms = comm.sms;
        const inbound = sms.direction === `inbound`;

        const action = !inbound && sms.actionId ? actionsById.get(String(sms.actionId)) : undefined;

        if (action) {
          if (action.type !== ActionType.ON_DEMAND) {
            actionsWithComms.add(String(action.id));

            return;
          }

          actionsReplacedByComms.add(String(action.id));
        }

        const failed = [`failed`, `suspended`].includes(sms.status?.toLowerCase());

        all.push({
          key: `sms-${sms.id}`,
          // Stands in for its on demand action too
          kinds: action ? [`comms`, `actions`] : [`comms`],
          at: new Date(sms.createdAt),
          icon: inbound ? ArrowUturnLeftIcon : failed ? ExclamationTriangleIcon : ChatBubbleLeftIcon,
          iconClass: failed ? `bg-orange-500` : `bg-blue-500`,
          title: inbound ? `${sms.contactName || sms.from || `Contact`} replied by SMS`
            : failed ? `SMS to ${sms.to} not sent` : `SMS sent to ${sms.to}`,
          detail: inbound ? null : failed ? sms.errorMessage || null : sms.body,
          body: inbound ? sms.body : null,
        });

        return;
      }

      if (comm.type === CommunicationType.INBOUND_EMAIL && comm.inboundEmail) {
        const reply = comm.inboundEmail;

        all.push({
          key: `inbound-email-${reply.id || i}`,
          kinds: [`comms`],
          at: new Date(reply.createdAt),
          icon: ArrowUturnLeftIcon,
          iconClass: `bg-blue-500`,
          title: `${reply.contactName || reply.from} replied by email`,
          detail: reply.subject || null,
          body: (reply.extractedReplyText || reply.textBody || ``).trim() || null,
        });
      }
    });

    actions.forEach(action => {
      if (actionsReplacedByComms.has(String(action.id))) return;

      const item = actionItem(action, safemode, now);

      if (actionsWithComms.has(String(action.id))) {
        item.kinds.push(`comms`);
      }

      all.push(item);
    });

    escalations.forEach(escalation => {
      const type = langauge.escalation.type[escalation.escalationType]?.label || escalation.escalationType;
      const link = (
        <WrappedLink
          to={ `/escalations/${escalation.id}/contact/${escalation.contactId}` }
          className={ linkClass }
        >
          { type }
        </WrappedLink>
      );
      const invoice = escalation.invoiceId ? (
        <InvoiceLink
          id={ escalation.invoiceId }
          number={ escalation.invoice?.xero_number }
        />
      ) : null;

      all.push({
        key: `escalation-raised-${escalation.id}`,
        kinds: [`escalations`],
        at: new Date(escalation.createdAt),
        icon: FlagIcon,
        iconClass: `bg-red-600`,
        title: <>{ `Escalation raised: ` }{ link }</>,
        detail: detailLine(
          escalation.assignedUser ? `Assigned to ${escalation.assignedUser.firstName} ${escalation.assignedUser.lastName}` : `Unassigned`,
          escalation.policyId ? escalation.policy?.title : `Manual`,
          invoice,
        ),
      });

      const closedAt = escalation.completedAt || escalation.cancelledAt;

      if (closedAt) {
        all.push({
          key: `escalation-closed-${escalation.id}`,
          kinds: [`escalations`],
          at: new Date(closedAt),
          icon: CheckIcon,
          iconClass: `bg-red-600`,
          title: <>{ escalation.completedAt ? `Escalation resolved: ` : `Escalation cancelled: ` }{ link }</>,
          detail: invoice,
        });
      }
    });

    notes.forEach(note => all.push({ ...noteItem(note, accountingName), kinds: [`notes`] }));

    // Logged calls and SMS without a note. The ones with a note come through as that note
    (activitiesData?.activities || [])
      .filter(activity => !activity.noteId)
      .forEach(activity => {
        const name = `${activity.userFirstName || ``} ${activity.userLastName || ``}`.trim() || `Someone`;

        all.push({
          key: `activity-${activity.id}`,
          kinds: [`notes`],
          at: new Date(activity.activityTime || activity.createdAt),
          icon: activity.type === ActivityType.PHONE_CALL ? PhoneIcon : activity.type === ActivityType.TEXT_MESSAGE ? ChatBubbleLeftIcon : EnvelopeIcon,
          iconClass: `bg-paidniceGreen-600`,
          title: (
            <span>
              <span className={ `font-medium` }>{ name }</span>
              { ` logged a ${activityLabel(activity.type, activity.callOutcome)}` }
            </span>
          ),
          detail: activity.invoiceId ? (
            <InvoiceLink
              id={ activity.invoiceId }
              number={ activity.invoiceNumber }
            />
          ) : null,
          body: activity.contactCallId ? (
            <CallTranscript
              organisationId={ activity.organisationId }
              contactId={ activity.contactId }
              contactCallId={ activity.contactCallId }
              callerName={ activity.userFirstName }
            />
          ) : null,
        });
      });

    pendingNotes
      .filter(pending => notes.length <= pending.notesBefore)
      .forEach(pending => all.push({
        key: `pending-${pending.id}`,
        kinds: [`notes`],
        at: pending.createdAt,
        icon: PencilIcon,
        iconClass: `bg-paidniceGreen-600`,
        title: `You added a note`,
        body: pending.content,
        timeLabel: `Sending...`,
        pending: true,
      }));

    return all.sort((a, b) => a.at.getTime() - b.at.getTime());
  }, [actionsData, commsData, escalations, notes, activitiesData, pendingNotes, accountingName, safemode]);

  const visibleItems = useMemo(() => items.filter(item => (showFuture || !item.future) &&
    (filter === `all` || item.kinds.includes(filter))), [items, filter, showFuture]);

  async function onSaveNote(content: string, mentionedUsers: string[], attachments: File[], syncAttachments: boolean) {
    const pending: PendingNote = { id: Date.now(), content, createdAt: new Date(), notesBefore: notes.length };

    setPendingNotes(current => [...current, pending]);
    setFollows(n => n + 1);

    const result = await postNewNote({
      organisationId: contact.organisationId,
      contactId: contact.id,
      content,
      mentionedUsers,
      attachments,
      syncAttachments,
    });

    if (`error` in result) {
      setPendingNotes(current => current.filter(p => p.id !== pending.id));
    }
  }

  function onActivitySaved() {
    setFollows(n => n + 1);
  }

  if (!contact) return null;

  const logButtonClass = `cursor-pointer h-[2.125rem] whitespace-nowrap rounded-md border border-gray-200 px-3 text-sm text-gray-700 hover:bg-gray-100`;

  return (
    <div className={ `flex h-[40rem] flex-col rounded-xl border border-gray-200 bg-white xl:h-full xl:min-h-0` }>
      <div className={ `flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3` }>
        <SegmentedControl
          filters={ filters }
          value={ filter }
          onChange={ setFilter }
        />
        <span className={ `flex items-center gap-3 whitespace-nowrap text-xs text-gray-500` }>
          <label className={ `flex cursor-pointer items-center gap-1.5` }>
            <input
              type={ `checkbox` }
              checked={ showFuture }
              onChange={ e => setShowFuture(e.target.checked) }
              className={ `h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-paidniceGreen-600 focus:ring-paidniceGreen-500` }
            />
            { `Show future` }
          </label>
          { `Oldest at the top` }
        </span>
      </div>

      <When condition={ !!contact.frozen }>
        <div className={ `shrink-0 bg-blue-400 px-4 py-2 text-center text-[0.8125rem] font-semibold text-white` }>
          { `This contact is frozen. No policies will apply to its invoices` }
        </div>
      </When>

      <ActivityFeedTimeline
        items={ visibleItems }
        timezone={ timezone }
        followKey={ `${filter}:${showFuture}:${follows}` }
        // Comms arrive before actions and would show raw until the action rows replace them, so wait for every source
        loading={ actionsLoading || escalationsLoading || commsLoading || activitiesLoading }
      />

      <div className={ `shrink-0 border-t border-gray-100 px-3 py-2.5` }>
        <NoteComposer
          compact
          submitLabel={ `Save note` }
          onSave={ onSaveNote }
          loading={ postingNote }
          trailing={ (
            <>
              <button
                type={ `button` }
                onClick={ () => setLogType(ActivityType.PHONE_CALL) }
                className={ logButtonClass }
              >
                { `Log call` }
              </button>
              <button
                type={ `button` }
                onClick={ () => setLogType(ActivityType.TEXT_MESSAGE) }
                className={ logButtonClass }
              >
                { `Log SMS` }
              </button>
            </>
          ) }
        />
      </div>

      <LogActivityModal
        open={ logType !== null }
        setOpen={ open => !open && setLogType(null) }
        organisationId={ contact.organisationId }
        contactId={ contact.id }
        initialType={ logType ?? ActivityType.PHONE_CALL }
        onSaved={ onActivitySaved }
      />
    </div>
  );
}
